/**
 * Vue Router configuration for the test application.
 *
 * Story 301: `/`. Story 302: `/configure`. Story 303: `/chat`.
 * Story 305: `/tools`. Story 306: `/health`. Story 307: `/errors`.
 */
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router';
import HomeView from './views/HomeView.vue';
import ConfigureView from './views/ConfigureView.vue';
import ChatView from './views/ChatView.vue';
import HealthView from './views/HealthView.vue';
import ToolsView from './views/ToolsView.vue';
import ErrorsView from './views/ErrorsView.vue';
import CallbackView from './views/CallbackView.vue';
import LoginView from './views/LoginView.vue';
import type { ChatProvider } from './services/chatProvider';
import type { HealthProvider } from './views/HealthView.vue';
import { loadPersistedConfig } from './services/configStore';
import { oauthAuthConfig } from './oauth.config';
import { makeBackendChatProvider, type BackendChatProviderConfig } from './services/backendChatProvider';
import { makeBackendHealthProvider } from './services/backendHealthProvider';

/**
 * Build the config the live `/chat` and `/health` routes connect with, from
 * persisted storage (API key) at navigation time so users see their latest
 * Configure-page settings without a page reload. Requests go to this app's
 * own same-origin backend (Story 015.3a/015.3b) — never a direct-browser
 * Anthropic client.
 */
function buildBackendConfig(): BackendChatProviderConfig {
  const stored = loadPersistedConfig();
  return {
    apiKey: (stored.apiKey ?? '').trim(),
    ...(stored.model !== undefined ? { model: stored.model } : {}),
    ...(stored.maxTokens !== undefined ? { maxTokens: stored.maxTokens } : {}),
  };
}

function makeDefaultChatProvider(): ChatProvider {
  return makeBackendChatProvider(buildBackendConfig());
}

function makeDefaultHealthProvider(): HealthProvider {
  return makeBackendHealthProvider(buildBackendConfig());
}

export const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: HomeView, meta: { title: 'Home' } },
  { path: '/configure', name: 'configure', component: ConfigureView, meta: { title: 'Configure' } },
  {
    path: '/chat',
    name: 'chat',
    component: ChatView,
    props: () => ({ provider: makeDefaultChatProvider() }),
    meta: { title: 'Chat' },
  },
  {
    path: '/tools',
    name: 'tools',
    component: ToolsView,
    meta: { title: 'Tools' },
  },
  {
    path: '/health',
    name: 'health',
    component: HealthView,
    props: () => ({ provider: makeDefaultHealthProvider() }),
    meta: { title: 'Health' },
  },
  {
    path: '/errors',
    name: 'errors',
    component: ErrorsView,
    meta: { title: 'Errors' },
  },
  {
    path: '/callback',
    name: 'callback',
    component: CallbackView,
    props: () => ({
      auth: oauthAuthConfig,
      navigate: (path: string) => router.replace(path),
    }),
    meta: { title: 'Callback', chromeless: true },
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { title: 'Log in', chromeless: true },
  },
];

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
});
